import { useCallback, useRef, useState } from 'react';

import { useSearchIndexUpdateListener } from '@/hooks/use-search-index-update-listener';
import { archivedSearchService } from '@/services/search-service';
import type { SearchResult } from '@/services/search-service/types';

import { useArchivedSearchIndex } from './use-archived-search-index';
import { buildSearchParams, type SearchQuery } from './use-search-core';

export const ARCHIVE_BROWSE_PAGE_SIZE = 50;

type BrowseState = {
  data: SearchResult[];
  isInitialLoading: boolean;
  hasMore: boolean;
};

const sortArrays = (_: string, v: unknown) => (Array.isArray(v) ? [...v].sort() : v);

// Offset-free growing-limit paging against the archived Orama index for the empty-term
// "browse" list (term search keeps useArchivedSearch's existing bounded fetch — see
// useInfiniteArchivedSearch). Each loadMore refetches with limit += PAGE_SIZE and replaces
// the list, so ranking/sort stay consistent without an append cursor.
//
// Race-condition handling is ref-based, matching useSearchCore's idiom (no AbortController):
// - activeQueryRef holds the latest serialized query; in-flight fetches check it after await
// - replaceGenerationRef bumps on every fetch (auto and grow) so a slower in-flight response
//   cannot overwrite a newer one (e.g. live replace at limit 100 after grow to 150)
// - loadingMoreRef / hasMoreRef guard duplicate or exhausted loadMore calls
// - lastFetchedQueryRef detects query changes inside the shared auto-fetch path so limit
//   resets to PAGE_SIZE on filter/sort changes, while live index updates keep the current limit
// - failed grow rolls limitRef back to the last successful window so retry does not skip a page
export const useArchivedBrowseSearch = (query: SearchQuery, enabled: boolean) => {
  const indexReady = useArchivedSearchIndex(enabled);
  const [state, setState] = useState<BrowseState>({
    data: [],
    isInitialLoading: true,
    hasMore: false,
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const activeQueryRef = useRef<string | undefined>(undefined);
  const lastFetchedQueryRef = useRef<string | undefined>(undefined);
  const limitRef = useRef(ARCHIVE_BROWSE_PAGE_SIZE);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const replaceGenerationRef = useRef(0);

  const serializedQuery = JSON.stringify(query, sortArrays);
  activeQueryRef.current = serializedQuery;

  const fetchResults = useCallback(
    async (mode: 'auto' | 'grow' = 'auto') => {
      const requestQuery = serializedQuery;
      const isGrow = mode === 'grow';
      let limitBeforeGrow: number | undefined;

      if (isGrow) {
        if (loadingMoreRef.current || !hasMoreRef.current) {
          return;
        }
        loadingMoreRef.current = true;
        setIsLoadingMore(true);
        limitBeforeGrow = limitRef.current;
        limitRef.current += ARCHIVE_BROWSE_PAGE_SIZE;
      } else if (lastFetchedQueryRef.current !== requestQuery) {
        limitRef.current = ARCHIVE_BROWSE_PAGE_SIZE;
      }

      replaceGenerationRef.current += 1;
      const generation = replaceGenerationRef.current;
      const requestLimit = limitRef.current;
      const isStale = () => activeQueryRef.current !== requestQuery || replaceGenerationRef.current !== generation;

      try {
        const params = buildSearchParams(query, { limit: requestLimit });
        const response = await archivedSearchService.search(params);
        if (isStale()) {
          return;
        }

        const hasMore = response.hits.length === requestLimit;
        hasMoreRef.current = hasMore;
        lastFetchedQueryRef.current = requestQuery;

        setState({
          data: response.hits,
          isInitialLoading: false,
          hasMore,
        });
      } catch (error) {
        console.error('Archived browse search failed:', error);
        // Only roll back when nothing newer has taken over limitRef; a superseded
        // grow must not resize the window a newer in-flight request already set.
        if (isGrow && limitBeforeGrow !== undefined && !isStale()) {
          limitRef.current = limitBeforeGrow;
        }
        if (!isGrow && !isStale()) {
          hasMoreRef.current = false;
          lastFetchedQueryRef.current = requestQuery;
          setState({ data: [], isInitialLoading: false, hasMore: false });
        }
      } finally {
        if (isGrow) {
          loadingMoreRef.current = false;
          setIsLoadingMore(false);
        }
      }
    },
    // query intentionally excluded: recreated when serializedQuery (content fingerprint) changes
    [serializedQuery]
  );

  const refetch = useCallback(() => {
    fetchResults('auto');
  }, [fetchResults]);

  useSearchIndexUpdateListener({
    service: archivedSearchService,
    refetch,
    enabled,
  });

  const loadMore = useCallback(() => {
    fetchResults('grow');
  }, [fetchResults]);

  return {
    data: state.data,
    isLoading: !indexReady || state.isInitialLoading,
    hasMore: state.hasMore,
    isLoadingMore,
    loadMore,
  };
};
